import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { callQuery, Lab, RISK_SCORES_FOR_CSN, RiskScore } from "../../../amplify/appsync/query";
import { PatientVisit } from "../../../amplify/appsync/search-patients";
import { DISPO_RISK_THRESHOLD } from "../../../utils/plot";
import { EpicRiskScoreModal } from "../EpicRiskScoreModal";
import { AfibRisk } from "./AfibRisk";
import { RiskConfig, RiskTextSquare } from "./RiskTextSquare";

import './RiskBar.scss';
import { dashboardLLMCache } from "../llm/cache";
import { getUserRole, UserRole } from "../../../utils/user";

/**
 * Horizontal bar showing the risks
 */
export const RiskBar = (props: {
    patientVisit: PatientVisit,
    labs: Lab[][],
    dispoAdmitRisk: any,
    setShowDispoRiskModal: any,
    showDispoRiskModal: boolean,
    tenant: string,
    user: any,
}) => {

    const [riskScores, setRiskScores] = useState<Record<string, RiskScore>>({});
    const [riskDetails, setRiskDetails] = useState<any>({});
    const [showEpicRiskScoreModal, setShowEpicRiskScoreModal] = useState<any>(false);

    const configuredRiskScores: RiskConfig[] = [
        {
            model: "Bounceup_24h",
            title: "24h Bounce Up Risk",
            threshold: 0.5,
        },
        {
            model: "Bounceup_72h",
            title: "72h Bounce Up Risk",
            threshold: 0.5,
        },
        {
            model: "Death_180d",
            title: "180d Mortality Risk",
            threshold: 0.5,
        },
        {
            model: "Revisit_3d",
            title: "3d Revisit Risk",
            threshold: 0.5,
        },
        {
            model: "Revisit_30d",
            title: "30d Revisit Risk",
            threshold: 0.5,
        },
    ];

    useEffect(() => {
        if (props.patientVisit) {
            fetchRiskScores(props.patientVisit);
        }
    }, [props.patientVisit])

    const fetchRiskScores = (currPatientVisit: PatientVisit) => {
        setRiskScores({});
        const riskScoresPromise = callQuery({
            operation: RISK_SCORES_FOR_CSN,
            csn: currPatientVisit.csn,
        });
        const riskDetailsPromise = dashboardLLMCache.get("risk_model", props.patientVisit, props.tenant)

        Promise.all([riskScoresPromise, riskDetailsPromise]).then((values) => {
            const items: any[] = values[0];
            const riskDetails = values[1];
            const riskScores: Record<string, RiskScore> = {};
            for (let item of items) {
                const riskScore: RiskScore = {
                    csn: currPatientVisit.csn,
                    prediction_time: item.prediction_time,
                    prediction_type: item.prediction_type,
                    prediction: item.prediction[0],
                }
                riskScores[item.prediction_type] = riskScore;
            }
            setRiskScores(riskScores);
            setRiskDetails(riskDetails);
        });
    }

    const admitRiskScore = useMemo(() => {
        if (riskScores) {
            if (riskScores["Admit_dispo"]) {
                return {
                    model: "Admit_dispo",
                    title: "Admit Risk",
                    threshold: 0.5,
                };
            } else if (riskScores["Admit_rooming"]) {
                return {
                    model: "Admit_rooming",
                    title: "Admit Risk",
                    threshold: 0.5,
                };
            } else if (riskScores["Admit_triage"]) {
                return {
                    model: "Admit_triage",
                    title: "Admit Triage",
                    threshold: 0.5,
                };
            } else {
                return null;
            }
        }
        return null;
    }, [riskScores]);

    const isAdmin = getUserRole(props.user) === UserRole.ADMIN;

    return (
        <Row className="mb-2 metrics-dashboard d-flex justify-content-between flex-wrap gx-2">
            {
                riskScores && riskScores["EPIC_REVISIT_SCORE"] && (
                    <Col className="mt-2" style={{ flex: '0 0 14.2857%' }}>
                        <RiskTextSquare config={{
                            title: '30-Day Revisit Risk',
                            threshold: 0.5,
                            model: "EPIC_REVISIT_SCORE",
                        }} adminOnly={true} riskScores={riskScores} riskDetails={riskDetails} isLoading={riskScores === null} onClick={() => { setShowEpicRiskScoreModal(!showEpicRiskScoreModal); } } patientVisit={props.patientVisit} />
                    </Col>
                )
            }
            {
                isAdmin && admitRiskScore && (
                    <Col className="mt-2" style={{ flex: '0 0 14.2857%' }}>
                        <RiskTextSquare config={admitRiskScore} patientVisit={props.patientVisit} riskScores={riskScores} riskDetails={riskDetails} isLoading={riskScores === null} adminOnly={true} />
                    </Col>
                )
            }
            {
                isAdmin && configuredRiskScores.map((config: any) => {
                    return riskScores && riskScores[config.model] ? (
                        <Col className="mt-2" style={{ flex: '0 0 14.2857%' }}>
                            <RiskTextSquare config={config} patientVisit={props.patientVisit} riskScores={riskScores} riskDetails={riskDetails} isLoading={riskScores === null} adminOnly={true} />
                        </Col>
                    ) : <></>
                })
            }
            <AfibRisk tenant={props.tenant} patientVisit={props.patientVisit} riskScores={riskScores} labs={props.labs} />
            <EpicRiskScoreModal showModal={showEpicRiskScoreModal} setShowModal={setShowEpicRiskScoreModal} patientVisit={props.patientVisit} />
        </Row>
    );
};
